import { v4 as uuidv4 } from 'uuid';

const CONSENT_COOKIE = 'tracking_consent';
const FBP_COOKIE = '_fbp';
const FBC_COOKIE = '_fbc';

// ─────────────────────────────────────────────
// Consent Management
// ─────────────────────────────────────────────

export type ConsentState = 'pending' | 'granted' | 'denied';

export function getConsentState(): ConsentState {
    if (typeof window === 'undefined') return 'pending';
    const stored = localStorage.getItem(CONSENT_COOKIE);
    if (stored === 'granted') return 'granted';
    if (stored === 'denied') return 'denied';
    return 'pending';
}

export function setConsentState(state: 'granted' | 'denied'): void {
    localStorage.setItem(CONSENT_COOKIE, state);
    if (state === 'granted') {
        initializePixel();
    }
}

export function hasConsent(): boolean {
    return getConsentState() === 'granted';
}

// ─────────────────────────────────────────────
// Cookie Helpers
// ─────────────────────────────────────────────

function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

export function getFbp(): string | null {
    return getCookie(FBP_COOKIE);
}

export function getFbc(): string | null {
    // Check URL for fbclid parameter
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid');
        if (fbclid) {
            return `fb.1.${Date.now()}.${fbclid}`;
        }
    }
    return getCookie(FBC_COOKIE);
}

// ─────────────────────────────────────────────
// Event ID Generation (for deduplication)
// ─────────────────────────────────────────────

export function generateEventId(): string {
    return uuidv4();
}

// ─────────────────────────────────────────────
// Pixel Initialization
// ─────────────────────────────────────────────

let pixelId: string | null = null;
let pixelInitialized = false;

export function setPixelId(id: string | null): void {
    pixelId = id;
    console.log('[Meta Pixel] Pixel ID set:', id);
}

export function getPixelId(): string | null {
    return pixelId;
}

export function isPixelInitialized(): boolean {
    return pixelInitialized;
}

export function initializePixel(): void {
    if (typeof window === 'undefined') return;
    if (pixelInitialized) {
        console.log('[Meta Pixel] Already initialized');
        return;
    }
    if (!pixelId) {
        console.log('[Meta Pixel] No pixel ID set');
        return;
    }

    console.log('[Meta Pixel] Initializing with ID:', pixelId);

    // Check if fbq already exists (prevent double loading)
    if ((window as any).fbq) {
        console.log('[Meta Pixel] fbq already exists, just init');
        (window as any).fbq('init', pixelId);
        (window as any).fbq('track', 'PageView');
        pixelInitialized = true;
        return;
    }

    // Create fbq function
    const fbq = function(...args: any[]) {
        if ((fbq as any).callMethod) {
            (fbq as any).callMethod.apply(fbq, args);
        } else {
            (fbq as any).queue.push(args);
        }
    };
    (fbq as any).push = fbq;
    (fbq as any).loaded = true;
    (fbq as any).version = '2.0';
    (fbq as any).queue = [];
    (window as any).fbq = fbq;
    (window as any)._fbq = fbq;

    // Load the Facebook Pixel script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    
    script.onload = () => {
        console.log('[Meta Pixel] Script loaded, calling init');
        if ((window as any).fbq) {
            (window as any).fbq('init', pixelId);
            (window as any).fbq('track', 'PageView');
            console.log('[Meta Pixel] Initialized and PageView sent');
        }
    };
    
    script.onerror = () => {
        console.error('[Meta Pixel] Failed to load script');
    };

    // Insert script
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    } else {
        document.head.appendChild(script);
    }

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    pixelInitialized = true;
    console.log('[Meta Pixel] Initialization complete');
}

// ─────────────────────────────────────────────
// Event Tracking
// ─────────────────────────────────────────────

interface TrackEventOptions {
    eventName: string;
    eventId: string;
    customData?: Record<string, any>;
    userData?: Record<string, any>;
}

export async function trackEvent(options: TrackEventOptions): Promise<void> {
    const { eventName, eventId, customData, userData } = options;

    // Track via Pixel (browser-side)
    if (pixelInitialized && typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', eventName, customData, { eventID: eventId });
        console.log('[Meta Pixel] Tracked:', eventName, eventId);
    } else {
        console.log('[Meta Pixel] Event skipped (not initialized):', eventName);
    }

    // Track via CAPI (server-side relay)
    try {
        const capiPayload = {
            event_name: eventName,
            event_time: Math.floor(Date.now() / 1000),
            event_id: eventId,
            event_source_url: window.location.href,
            user_data: {
                fbp: getFbp(),
                fbc: getFbc(),
                ...userData,
            },
            custom_data: customData,
        };

        await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/tracking/capi/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(capiPayload),
        });
        console.log('[Meta CAPI] Sent:', eventName, eventId);
    } catch (error) {
        console.warn('[Meta CAPI] Failed:', error);
    }
}

// ─────────────────────────────────────────────
// Pre-built Event Helpers
// ─────────────────────────────────────────────

export function trackPageView(): void {
    if (!pixelInitialized) {
        console.log('[Meta Pixel] trackPageView skipped - not initialized');
        return;
    }
    if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'PageView');
        console.log('[Meta Pixel] PageView tracked');
    } else {
        console.log('[Meta Pixel] trackPageView skipped - fbq not available');
    }
}

export function trackViewContent(params: {
    contentId: string;
    contentName: string;
    contentCategory?: string;
    value?: number;
    currency?: string;
}): void {
    const eventId = generateEventId();
    trackEvent({
        eventName: 'ViewContent',
        eventId,
        customData: {
            content_ids: [params.contentId],
            content_name: params.contentName,
            content_category: params.contentCategory,
            content_type: 'product',
            value: params.value,
            currency: params.currency || 'DZD',
        },
    });
}

export function trackAddToCart(params: {
    contentId: string;
    contentName: string;
    value: number;
    quantity: number;
    currency?: string;
}): void {
    const eventId = generateEventId();
    trackEvent({
        eventName: 'AddToCart',
        eventId,
        customData: {
            content_ids: [params.contentId],
            content_name: params.contentName,
            content_type: 'product',
            value: params.value,
            currency: params.currency || 'DZD',
            contents: [{ id: params.contentId, quantity: params.quantity, item_price: params.value }],
        },
    });
}

export function trackInitiateCheckout(params: {
    contentIds: string[];
    value: number;
    numItems: number;
    currency?: string;
}): void {
    const eventId = generateEventId();
    trackEvent({
        eventName: 'InitiateCheckout',
        eventId,
        customData: {
            content_ids: params.contentIds,
            content_type: 'product',
            value: params.value,
            currency: params.currency || 'DZD',
            num_items: params.numItems,
        },
    });
}

export function trackPurchase(params: {
    orderId: string;
    contentIds: string[];
    contents: Array<{ id: string; quantity: number; item_price: number }>;
    value: number;
    numItems: number;
    currency?: string;
    customerPhone?: string;
    customerName?: string;
}): void {
    const eventId = generateEventId();
    trackEvent({
        eventName: 'Purchase',
        eventId,
        customData: {
            content_ids: params.contentIds,
            content_type: 'product',
            contents: params.contents,
            value: params.value,
            currency: params.currency || 'DZD',
            num_items: params.numItems,
            order_id: params.orderId,
        },
        userData: {
            ph: params.customerPhone,
            fn: params.customerName?.split(' ')[0],
            ln: params.customerName?.split(' ').slice(1).join(' '),
        },
    });
}
